import { useFindMany } from "@gadgetinc/react";
import { ChevronRightIcon } from "@heroicons/react/16/solid";
import { PencilIcon } from "@heroicons/react/24/outline";
import clsx from "clsx";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { api } from "../../api";
import { calculateRate } from "../../functions/calculateRate";
import { formatDateTime } from "../../functions/date-utils";
import { fetchCampaignAnalyticsStats } from "../../functions/fetchCampaignAnalyticsStats";
import LoadingText from "../LoadingText";
import { Badge } from "../tailwind/badge";
import { Button } from "../tailwind/button";
import { Divider } from "../tailwind/divider";
import { Link } from "../tailwind/link";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../tailwind/table";
import { Text } from "../tailwind/text";

export function ViewPastCampaigns({
  customerId,
  shopId,
  euroRate,
  className,
  isMobile,
  integrations,
}) {
  const navigate = useNavigate();
  const [campaignStats, setCampaignStats] = useState({});

  // Fetch campaigns without metrics
  const [{ data: lastCampaigns, fetching }] = useFindMany(api.campaign, {
    filter: {
      customerId: { equals: customerId },
      isArchived: { notEquals: true },
      trigger: { equals: "manual" },
      sendings: {
        some: {
          status: { equals: "completed" },
        },
      },
    },
    sort: {
      updatedAt: "Descending",
    },
    select: {
      id: true,
      name: true,
      scheduledDate: true,
      createdAt: true,
    },
    first: 10,
  });

  const lastCampaignsSent = useMemo(() => {
    return (lastCampaigns ?? [])
      .filter((campaign) => campaign.scheduledDate)
      .sort((a, b) => new Date(b.scheduledDate) - new Date(a.scheduledDate))
      .slice(0, 3);
  }, [lastCampaigns]);

  // Fetch stats for each campaign using views (from = createdAt, to = now)
  useEffect(() => {
    if (!customerId || !lastCampaignsSent.length) {
      return;
    }

    setCampaignStats({});

    lastCampaignsSent.forEach((campaign) => {
      const fromDate = new Date(campaign.createdAt);
      fromDate.setHours(0, 0, 0, 0);

      fetchCampaignAnalyticsStats({
        customerId,
        shopId,
        euroRate,
        campaignId: campaign.id,
        selectedDateFilter: "Between",
        startDate: fromDate,
        endDate: new Date(),
        onUpdate: (updater) => {
          setCampaignStats((prev) => ({
            ...prev,
            [campaign.id]: updater(prev[campaign.id] || {}),
          }));
        },
      });
    });
  }, [lastCampaignsSent, customerId, shopId]);

  return (
    <div className={clsx(className, "border rounded-xl p-3 shadow bg-white")}>
      <Link onClick={() => navigate("/campaigns")}>
        <div className="flex items-center hover:underline hover:cursor-pointer">
          <Text className="text-lg font-bold mb-2">Latest campaigns</Text>
          <ChevronRightIcon className="h-6 w-6 mb-1" />
        </div>
      </Link>
      <Divider />

      <Table dense>
        <TableHead>
          <TableRow>
            {!isMobile && <TableHeader>Status</TableHeader>}
            <TableHeader>Campaign</TableHeader>
            <TableHeader>Last sending</TableHeader>
            <TableHeader className="text-center">Audience</TableHeader>
            {!isMobile && (
              <>
                <TableHeader className="text-center">Delivered</TableHeader>
                <TableHeader className="text-center">Open rate</TableHeader>
                {integrations.shopify && (
                  <TableHeader className="text-center">Turnover</TableHeader>
                )}
                <TableHeader className="text-center"></TableHeader>
              </>
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {fetching ? (
            <TableRow>
              <TableCell colSpan={7}>
                <LoadingText className="my-3 h-[24px]" />
              </TableCell>
            </TableRow>
          ) : lastCampaignsSent.length > 0 ? (
            lastCampaignsSent.map((campaign) => (
              <CampaignRow
                key={campaign.id}
                campaign={campaign}
                stats={campaignStats[campaign.id]}
                integrations={integrations}
                isMobile={isMobile}
                navigate={navigate}
              />
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={7}>
                <Text className="text-center py-4 text-lg text-gray-700">
                  Start sending campaigns 📢
                </Text>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
}

function CampaignRow({ campaign, stats, integrations, isMobile, navigate }) {
  const read = stats?.readBetween ?? 0;
  const delivered = stats?.deliveredBetween ?? 0;
  const messagesCount = stats?.messagesCountBetween ?? 0;
  const turnover = stats?.turnoverBetween ?? 0;

  const openRate =
    read > 0 && delivered > 0
      ? calculateRate(read, delivered).toFixed(0)
      : null;

  return (
    <TableRow
      key={campaign.id}
      href={`/analytics/${campaign.id}`}
      className="group hover:bg-muted transition"
    >
      {!isMobile && (
        <TableCell>
          <Badge color="lime">Sent</Badge>
        </TableCell>
      )}
      <TableCell>{campaign.name}</TableCell>
      <TableCell>
        {campaign.scheduledDate
          ? isMobile
            ? formatDateTime(new Date(campaign.scheduledDate), "shortDate")
            : formatDateTime(
                new Date(campaign.scheduledDate),
                "shortTimeMediumDate",
              )
          : "-"}
      </TableCell>
      <TableCell className="text-center">{messagesCount || "-"}</TableCell>
      {!isMobile && (
        <>
          <TableCell className="text-center">{delivered || "-"}</TableCell>
          <TableCell className="text-center">
            {openRate ? `${openRate}%` : "-"}
          </TableCell>
          {integrations.shopify && (
            <TableCell className="text-center">
              {turnover > 0 ? `${turnover.toFixed(0)}€` : "-"}
            </TableCell>
          )}
          <TableCell className="opacity-0 group-hover:opacity-100 transition-opacity duration-200">
            <Button
              plain
              className="h-[25px]"
              aria-label="Edit campaign"
              onClick={() => {
                navigate(`/campaigns/edit/${campaign.id}`);
              }}
            >
              <PencilIcon className="w-4" />
            </Button>
          </TableCell>
        </>
      )}
    </TableRow>
  );
}
